import React from "react";
import { Box, Chip } from "@mui/material";
import { AssetFinanceUrl } from "../../../config";
import DynamicTableForJson from "../../../data/DynamicTable/dynamicTableForJson";
import VoluntaryRepossessionForm from "./form";

const base_url = AssetFinanceUrl.uri;

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

// VehicleMake/VehicleModel on the view are only populated for some asset
// types — for the rest (and today, it seems, for all of them) the vehicle
// details live inside the AssetMetadata JSON blob captured when the asset
// was registered, so fall back to parsing that.
const getAssetLabel = (row) => {
  if (row.VehicleMake || row.VehicleModel) {
    return [row.VehicleMake, row.VehicleModel].filter(Boolean).join(" ");
  }
  try {
    const meta = row.AssetMetadata ? JSON.parse(row.AssetMetadata) : null;
    if (meta) {
      const parts = [meta.make, meta.vehicleModel].filter(Boolean);
      if (parts.length) {
        return meta.regNumber ? `${parts.join(" ")} (${meta.regNumber})` : parts.join(" ");
      }
    }
  } catch (error) {
    // fall through to the generic label below
  }
  return row.CollateralAssetId ? `Asset #${row.CollateralAssetId}` : "-";
};

const STATUS_COLORS = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error",
  CANCELLED: "default",
};

const rightsTourSteps = [
  {
    target: ".dynamic-table-header",
    content:
      "This is the Voluntary Repossession page, where you can request the voluntary repossession of a customer's collateral asset for a checker to approve.",
    placement: "top",
    disableBeacon: true,
  },
  {
    target: ".dynamic-table-add-button",
    content:
      "Click this button to search for a customer, select their asset and request a new voluntary repossession.",
    placement: "right",
  },
  {
    target: ".dynamic-table-actions",
    content: "Approve or reject a pending voluntary repossession request from this dropdown.",
    placement: "left",
  },
];

const columns = [
  { field: "Id", headerName: "Request ID", width: 100 },
  { field: "CustomerName", headerName: "Customer", flex: 1 },
  { field: "CustomerPhoneNumber", headerName: "Phone", flex: 1 },
  {
    field: "VehicleMake",
    headerName: "Asset",
    flex: 1,
    valueGetter: (params) => getAssetLabel(params.row),
  },
  {
    field: "IndicativeValue",
    headerName: "Indicative Value",
    flex: 1,
    valueGetter: (params) => formatCurrency(params.row.IndicativeValue),
  },
  { field: "LinkedLoanId", headerName: "Loan ID", width: 100 },
  {
    field: "LinkedLoanBalance",
    headerName: "Loan Balance",
    flex: 1,
    valueGetter: (params) => formatCurrency(params.row.LinkedLoanBalance),
  },
  { field: "Reason", headerName: "Reason", flex: 1 },
  { field: "Narration", headerName: "Narration", flex: 1 },
  { field: "CreatedByUsernameName", headerName: "Requested By", flex: 1 },
  { field: "DateCreated", headerName: "Date Requested", flex: 1 },
  {
    field: "RequestStatus",
    headerName: "Status",
    width: 130,
    renderCell: (params) => (
      <Chip
        label={params.row.RequestStatus || "Pending"}
        size="small"
        color={STATUS_COLORS[String(params.row.RequestStatus || "").toUpperCase()] || "warning"}
        sx={{ fontWeight: 600, fontSize: "0.7rem" }}
      />
    ),
  },
];

const VoluntaryRepossessionTable = () => {
  const actions = {
    entity: localStorage.getItem("SelectedRight"),
    add: {
      key: "ADD",
      button_name: "New Voluntary Repossession",
      Show_Button: true,
      permission: "CreatePermission",
    },
    ApproveVoluntaryRepossession: {
      key: "APPROVE",
      button_name: "Approve",
      Show_Button: true,
      permission: "ActivatePermission",
    },
    RejectVoluntaryRepossession: {
      key: "REJECT",
      button_name: "Reject",
      Show_Button: true,
      permission: "ActivatePermission",
    },
    dataObject: {
      ignoreFields: [
        "CollateralAssetId",
        "CustomerId",
        "CompanyId",
        "AssetType",
        "AssetSubType",
        "AssetStatusBefore",
        "AssetStatusAfter",
        "IsApproved",
        "IsCancelled",
        "CreatedBy",
        "UpdatedBy",
        "UpdatedDate",
        "CurrentAssetStatus",
        "VehicleModelId",
        "AssetMetadata",
        "LinkedLoanAmount",
      ],
      objectName: "voluntaryRepossessionByColumnValues",
      parameters: { isApproved: 0, isCancelled: 0 },
      inputObjectName: "voluntaryRepossession",
    },
    showStatus: true,
  };

  return (
    <Box m="20px">
      <DynamicTableForJson
        title="Voluntary Repossession"
        subtitle="Request voluntary repossession of customer collateral assets and approve those pending review"
        FormComponent={VoluntaryRepossessionForm}
        columns={columns}
        base_url={base_url}
        actions={actions}
        tourSteps={rightsTourSteps}
      />
    </Box>
  );
};

export default VoluntaryRepossessionTable;
